// src/App.tsx
import React, { ReactNode, Suspense, lazy, useState, useRef, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/Toast";
import PushToastBridge from "./components/PushToastBridge";
import { ConfirmProvider } from "./components/ConfirmModal";
import { BTN_SECONDARY_SM } from "./constants/ui";
import { useTierPromotion } from "./hooks/useTierPromotion";
import TierPromotionBanner from "./components/TierPromotionBanner";
import { usePushNotification } from "./hooks/usePushNotification";

// ✅ 코드 스플리팅: 페이지별로 청크 분리
// 공용 페이지 (즉시 로드)
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FindUsernamePage from "./pages/FindUsernamePage";
import PrivacyPage from "./pages/PrivacyPage";
import AccountDeletionPage from "./pages/AccountDeletionPage";
import TermsPage from "./pages/TermsPage";

// 교사 전용 페이지 (lazy loading)
const TeacherDashboardPage = lazy(() => import("./pages/TeacherDashboardPage"));
const AsyncExamUploadPage = lazy(() => import("./pages/AsyncExamUploadPage"));
const DispatchListPage = lazy(() => import("./pages/DispatchListPage"));
const StudentListPage = lazy(() => import("./pages/StudentListPage"));
const StudentRegistrationPage = lazy(() => import("./pages/StudentRegistrationPage"));
const ReportReviewPage = lazy(() => import("./pages/ReportReviewPage"));
const BatchReportReviewPage = lazy(() => import("./pages/BatchReportReviewPage"));

// 공통 인증 페이지 (lazy loading)
const StudentAnalysisPage = lazy(() => import("./pages/StudentAnalysisPage"));
const StudentHomePage = lazy(() => import("./pages/StudentHomePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

// 학부모 전용 페이지 (lazy loading)
const ParentHomePage = lazy(() => import("./pages/ParentHomePage"));
const ParentReportListPage = lazy(() => import("./pages/ParentReportListPage"));
const ParentReportDetailPage = lazy(() => import("./pages/ParentReportDetailPage"));
const ParentGrowthPage = lazy(() => import("./pages/ParentGrowthPage"));
const ParentProfilePage = lazy(() => import("./pages/ParentProfilePage"));

// 출석 관련 페이지 (lazy loading)
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const ClassManagementPage = lazy(() => import("./pages/ClassManagementPage"));
const KioskPage = lazy(() => import("./pages/KioskPage"));

// 크레딧 관련 페이지 (lazy loading)
const CreditChargePage     = lazy(() => import("./pages/CreditChargePage"));
const CreditSuccessPage    = lazy(() => import("./pages/CreditSuccessPage"));
const CreditFailPage       = lazy(() => import("./pages/CreditFailPage"));
const CreditHistoryPage    = lazy(() => import("./pages/CreditHistoryPage"));
const CreditManagementPage = lazy(() => import("./pages/CreditManagementPage"));
const RandomBoxPage        = lazy(() => import("./pages/RandomBoxPage"));
const CPManagementPage     = lazy(() => import("./pages/CPManagementPage"));

type AuthUser = {
  id: number;
  name: string;
  username: string;
  email: string;
  role?: string;
};

function useAuthUser(): AuthUser | null {
  // useMemo는 성능 최적화용이지만, localStorage는 이미 충분히 빠름
  // 의존성 배열이 비어있으면 localStorage 변경을 감지하지 못하므로
  // 단순 함수 호출로 변경하여 항상 최신 값을 읽도록 함
  try {
    const raw = localStorage.getItem("auth_user");
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function getHomePathByRole(user: AuthUser | null): string {
  // auth_user 없으면 일단 교사용으로 기본 이동 (MVP 기준)
  if (!user) return "/teacher";
  if (user.role === "teacher" || user.role === "director") return "/teacher";
  if (user.role === "parent") return "/parent";
  // 그 외는 기본 student
  return "/student";
}

/**
 * ✅ 인증 필요한 라우트 보호용 컴포넌트
 * - isAuthenticated가 false이면 /login으로 리다이렉트
 */
const PrivateRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

/**
 * ✅ 교사 전용 라우트
 * - 로그인 + role === "teacher" 인 경우만 접근 허용
 */
const TeacherRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const authUser = useAuthUser();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (authUser?.role !== "teacher" && authUser?.role !== "director") {
    const home = getHomePathByRole(authUser);
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
};

/**
 * ✅ 앱 내부 공통 상단바
 * - 데스크톱: 기존 가로 네비 유지
 * - 모바일: 앱바(로고 + 제목 + 햄버거) + 가로 탭바
 */
const TEACHER_TAB_ITEMS = [
  { to: "/teacher", label: "대시보드", match: (p: string) => p === "/teacher" },
  { to: "/exam-upload", label: "시험업로드", match: (p: string) => p.startsWith("/exam-upload") },
  { to: "/classes", label: "수업관리", match: (p: string) => p.startsWith("/classes") },
  { to: "/students", label: "학생관리", match: (p: string) => p.startsWith("/students") },
];

const DIRECTOR_TAB_ITEMS = [
  { to: "/teacher", label: "대시보드", match: (p: string) => p === "/teacher" },
  { to: "/students", label: "학생관리", match: (p: string) => p.startsWith("/students") },
  { to: "/classes", label: "수업관리", match: (p: string) => p.startsWith("/classes") },
  { to: "/attendance", label: "출석관리", match: (p: string) => p.startsWith("/attendance") },
  { to: "/exam-upload", label: "시험업로드", match: (p: string) => p.startsWith("/exam-upload") },
  { to: "/analysis", label: "분석내역", match: (p: string) => p.startsWith("/analysis") },
  { to: "/dispatch", label: "발송관리", match: (p: string) => p.startsWith("/dispatch") },
  { to: "/credit-management", label: "크레딧관리", match: (p: string) => p.startsWith("/credit-management") },
  { to: "/cp-management", label: "CP관리", match: (p: string) => p.startsWith("/cp-management") },
];

const PARENT_TAB_ITEMS = [
  { to: "/parent",         label: "홈",      match: (p: string) => p === "/parent" },
  { to: "/parent/reports", label: "리포트",  match: (p: string) => p.startsWith("/parent/reports") },
  { to: "/parent/growth",  label: "성장현황", match: (p: string) => p.startsWith("/parent/growth") },
  { to: "/parent/credit",  label: "크레딧",  match: (p: string) => p.startsWith("/parent/credit") },
];

const TEACHER_MENU_ITEMS = [
  { to: "/classes", label: "수업관리" },
  { to: "/students", label: "학생관리" },
];

const PAGE_TITLES: Record<string, string> = {
  "/teacher": "대시보드",
  "/exam-upload": "시험 업로드",
  "/analysis": "분석 내역",
  "/dispatch": "발송 관리",
  "/classes": "수업 관리",
  "/students": "학생 관리",
  "/students/register": "학생 등록",
  "/attendance":      "출석 관리",
  "/kiosk":           "출석 키오스크",
  "/credit-management": "크레딧 관리",
  "/cp-management":     "CP 관리",
  "/parent/credit":     "크레딧 내역",
  "/parent/credit/charge": "크레딧 충전",
  "/reports": "보고서",
  "/parent": "홈",
  "/parent/reports": "리포트",
  "/parent/growth": "성장현황",
  "/profile": "마이페이지",
};

function getPageTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith("/reports/") && pathname.includes("/review")) return "보고서 검토";
  if (pathname.startsWith("/reports/batch-review")) return "일괄 검토";
  if (pathname.startsWith("/exam-upload")) return "시험 업로드";
  if (pathname.startsWith("/classes")) return "수업 관리";
  if (pathname.startsWith("/students")) return "학생 관리";
  if (pathname.startsWith("/parent/reports/")) return "리포트 상세";
  return "새로운 연구소";
}


const BellButton: React.FC<{
  isSubscribed: boolean; isBlocked: boolean; isLoading: boolean;
  subscribe: () => void; unsubscribe: () => void;
  size: number; spinnerSize: number;
}> = ({ isSubscribed, isBlocked, isLoading, subscribe, unsubscribe, size, spinnerSize }) => (
  <button
    onClick={isBlocked ? undefined : (isSubscribed ? unsubscribe : subscribe)}
    disabled={isLoading || isBlocked}
    title={isBlocked ? "알림이 차단되어 있습니다" : isSubscribed ? "알림 구독 중 (클릭하여 해제)" : "알림 구독하기"}
    className={`flex items-center justify-center rounded-xl transition-colors ${isSubscribed ? "bg-teal-50 text-teal-600" : "text-slate-400 hover:bg-slate-100"} ${isBlocked ? "cursor-not-allowed opacity-50" : ""}`}
    style={{ width: size, height: size }}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full" style={{ width: spinnerSize, height: spinnerSize, border: "2px solid rgba(31,135,114,.2)", borderTopColor: "#1F8772" }} />
    ) : isSubscribed ? (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.7 21a2 2 0 0 1-3.4 0"/>
      </svg>
    ) : (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13.7 21a2 2 0 0 1-3.4 0"/>
        <path d="M17.5 17H3s3-2 3-9a6 6 0 0 1 .34-2"/>
        <path d="M13.7 3A6 6 0 0 1 18 8c0 3.25-.82 5.71-1.71 7.37"/>
        <line x1="2" y1="2" x2="22" y2="22"/>
      </svg>
    )}
  </button>
);

const AppTopBar: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const authUser = useAuthUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isSupported, isSubscribed, isBlocked, isLoading: pushLoading, subscribe, unsubscribe } = usePushNotification();

  const isStaff = authUser?.role === 'teacher' || authUser?.role === 'director';
  const menuRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // 페이지 이동 시 메뉴 닫기
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // 활성 탭 자동 스크롤
  useEffect(() => {
    if (!tabBarRef.current) return;
    const active = tabBarRef.current.querySelector("[data-active='true']") as HTMLElement | null;
    if (active) {
      active.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [location.pathname]);

  const role = authUser?.role;

  // 교사 플로우
  const inTeacherApp =
    (role === "teacher" || role === "director") && (
      location.pathname.startsWith("/teacher") ||
      location.pathname.startsWith("/exam-upload") ||
      location.pathname.startsWith("/dispatch") ||
      location.pathname.startsWith("/analysis") ||
      location.pathname.startsWith("/students") ||
      location.pathname.startsWith("/reports") ||
      location.pathname.startsWith("/attendance") ||
      location.pathname.startsWith("/classes") ||
      location.pathname.startsWith("/credit-management") ||
      location.pathname.startsWith("/cp-management") ||
      location.pathname.startsWith("/profile")
    );

  // 학부모 플로우
  const inParentApp =
    role === "parent" && (
      location.pathname.startsWith("/parent") ||
      location.pathname.startsWith("/credit")
    );

  // 학부모 홈/리포트 페이지는 자체 헤더 + 하단 탭바를 사용하므로 AppTopBar 미표시
  const isCustomParentPage = role === "parent" && (
    location.pathname === "/parent" ||
    location.pathname.startsWith("/parent/reports") ||
    location.pathname.startsWith("/parent/growth") ||
    location.pathname.startsWith("/parent/credit") ||
    location.pathname.startsWith("/parent/profile")
  );

  if (!isAuthenticated || (!inTeacherApp && !inParentApp) || isCustomParentPage) return null;

  const TAB_ITEMS = role === "parent" ? PARENT_TAB_ITEMS : role === "director" ? DIRECTOR_TAB_ITEMS : TEACHER_TAB_ITEMS;
  const MENU_ITEMS = role === "parent" ? [] : TEACHER_MENU_ITEMS;
  const homeLink = role === "parent" ? "/parent" : "/teacher";

  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="relative sticky top-0 z-50" ref={menuRef}>
      {/* ── 앱바 ── */}
      <div className="border-b border-[#57c4c6]/20 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          {/* 좌측: 로고 */}
          <Link to={homeLink} className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt="새로운 연구소"
              className="h-8 w-8 rounded-xl object-cover ring-1 ring-slate-200"
            />
            {/* 데스크톱: 서비스명 / 모바일: 숨김 (제목이 중앙에 표시됨) */}
            <span className="hidden text-sm font-semibold text-[#0f172a] md:block">
              새로운 연구소
            </span>
          </Link>

          {/* 모바일 중앙: 현재 페이지 제목 */}
          <span className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-[#0f172a] md:hidden">
            {pageTitle}
          </span>

          {/* 데스크톱: 기존 가로 네비 */}
          <div className="hidden items-center gap-1 md:flex">
            {TAB_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                  item.match(location.pathname)
                    ? "bg-[#57c4c6]/10 text-[#2aa7aa]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/profile"
              className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                location.pathname === "/profile"
                  ? "bg-[#57c4c6]/10 text-[#2aa7aa]"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              마이페이지
            </Link>
            {isStaff && isSupported && (
              <BellButton isSubscribed={isSubscribed} isBlocked={isBlocked} isLoading={pushLoading}
                subscribe={subscribe} unsubscribe={unsubscribe} size={32} spinnerSize={16} />
            )}
            <button
              onClick={() => {
                logout();
                navigate("/", { replace: true });
              }}
              className={`ml-2 ${BTN_SECONDARY_SM}`}
            >
              로그아웃
            </button>
          </div>

          {/* 모바일 우측: 알림 벨 + 햄버거 버튼 */}
          <div className="md:hidden flex items-center gap-1">
            {isStaff && isSupported && (
              <BellButton isSubscribed={isSubscribed} isBlocked={isBlocked} isLoading={pushLoading}
                subscribe={subscribe} unsubscribe={unsubscribe} size={36} spinnerSize={15} />
            )}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 transition-colors hover:bg-slate-100"
              aria-label="메뉴"
            >
              {menuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── 모바일 가로 탭바 ── */}
      <div
        ref={tabBarRef}
        className="overflow-x-auto border-b border-slate-100 bg-white scrollbar-hide md:hidden"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="flex min-w-full">
          {TAB_ITEMS.map((item) => {
            const active = item.match(location.pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-active={active}
                className={`relative flex-1 px-3 py-2.5 text-center text-sm font-semibold whitespace-nowrap transition-colors ${
                  active
                    ? "text-[#2aa7aa]"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                {item.label}
                {active && (
                  <span className="absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-[#2aa7aa]" />
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── 드롭다운 메뉴 (탭바 아래에 위치, 탭바와 겹치지 않음) ── */}
      {menuOpen && (
        <div className="absolute right-4 top-full mt-1 w-44 overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-slate-200 md:hidden">
          {MENU_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="block px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              {item.label}
            </Link>
          ))}
          <div className="border-t border-slate-100">
            <div className="px-4 py-2.5 text-xs text-slate-400">
              {authUser?.name || authUser?.username}
            </div>
            <Link
              to="/profile"
              className="block px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              마이페이지
            </Link>
            <button
              onClick={() => {
                logout();
                navigate("/", { replace: true });
              }}
              className="w-full px-4 py-3 text-left text-sm font-medium text-rose-500 transition-colors hover:bg-rose-50"
            >
              로그아웃
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * 로딩 스피너 컴포넌트
 */
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      <p className="mt-4 text-gray-600">로딩 중...</p>
    </div>
  </div>
);

/**
 * ✅ 실제 라우트 정의
 */
const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const authUser = useAuthUser();
  const homePath = getHomePathByRole(authUser);

  const isStudent = isAuthenticated && authUser?.role === "student";
  const { queue, dismissAll } = useTierPromotion(isStudent);

  return (
    <>
      {queue.length > 0 && (
        <TierPromotionBanner queue={queue} onDismissAll={dismissAll} />
      )}
      <AppTopBar />

      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* 랜딩: 누구나 접근 가능 */}
        <Route path="/" element={<LandingPage />} />

        {/* 로그인/회원가입: 이미 로그인 상태면 역할에 맞는 홈으로 보내기 */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to={homePath} replace /> : <LoginPage />
          }
        />
        <Route
          path="/signup"
          element={
            isAuthenticated ? (
              <Navigate to={homePath} replace />
            ) : (
              <SignupPage />
            )
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/find-username" element={<FindUsernamePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* Play 필수: 비로그인으로 접근 가능한 계정 삭제 안내 */}
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* 교사 대시보드(교사 전용) */}
        <Route
          path="/teacher"
          element={
            <TeacherRoute>
              <TeacherDashboardPage />
            </TeacherRoute>
          }
        />

        {/* 학생 홈 (인증 필요) */}
        <Route
          path="/student"
          element={
            <PrivateRoute>
              <StudentHomePage />
            </PrivateRoute>
          }
        />

        {/* 랜덤 박스 (학생 전용) */}
        <Route
          path="/randombox"
          element={
            <PrivateRoute>
              <RandomBoxPage />
            </PrivateRoute>
          }
        />

        {/* 학부모 페이지 (인증 필요) */}
        <Route
          path="/parent"
          element={
            <PrivateRoute>
              <ParentHomePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/parent/reports"
          element={
            <PrivateRoute>
              <ParentReportListPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/parent/reports/:id"
          element={
            <PrivateRoute>
              <ParentReportDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/parent/growth"
          element={
            <PrivateRoute>
              <ParentGrowthPage />
            </PrivateRoute>
          }
        />

        {/* 앱 기능 페이지 */}
        {/* 시험 업로드 / 발송 관리는 교사 전용 */}
        <Route
          path="/exam-upload"
          element={
            <TeacherRoute>
              <AsyncExamUploadPage />
            </TeacherRoute>
          }
        />
        <Route
          path="/dispatch"
          element={
            <TeacherRoute>
              <DispatchListPage />
            </TeacherRoute>
          }
        />

        {/* 수업 관리 (교사 전용) */}
        <Route
          path="/classes"
          element={
            <TeacherRoute>
              <ClassManagementPage />
            </TeacherRoute>
          }
        />

        {/* 학생 관리 (교사 전용) */}
        <Route
          path="/students"
          element={
            <TeacherRoute>
              <StudentListPage />
            </TeacherRoute>
          }
        />
        <Route
          path="/students/register"
          element={
            <TeacherRoute>
              <StudentRegistrationPage />
            </TeacherRoute>
          }
        />

        {/* 보고서 검토/수정 (교사 전용) */}
        <Route
          path="/reports/:id/review"
          element={
            <TeacherRoute>
              <ReportReviewPage />
            </TeacherRoute>
          }
        />
        <Route
          path="/reports/batch-review"
          element={
            <TeacherRoute>
              <BatchReportReviewPage />
            </TeacherRoute>
          }
        />

        {/* 분석 히스토리는 모든 로그인 유저(교사/학생/학부모) 공용 */}
        <Route
          path="/analysis"
          element={
            <PrivateRoute>
              <StudentAnalysisPage />
            </PrivateRoute>
          }
        />

        {/* 마이페이지 (모든 로그인 유저 공용) */}
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          }
        />

        {/* 출석 관리 (교사 전용) */}
        <Route
          path="/attendance"
          element={
            <TeacherRoute>
              <AttendancePage />
            </TeacherRoute>
          }
        />

        {/* 키오스크 — 인증 불필요, AppTopBar 미표시 */}
        <Route path="/kiosk" element={<KioskPage />} />

        {/* 크레딧 관리 (원장 전용) */}
        <Route
          path="/credit-management"
          element={
            <TeacherRoute>
              <CreditManagementPage />
            </TeacherRoute>
          }
        />

        {/* CP 관리 (원장 전용) */}
        <Route
          path="/cp-management"
          element={
            <TeacherRoute>
              <CPManagementPage />
            </TeacherRoute>
          }
        />

        {/* 크레딧 (학부모 전용) */}
        <Route
          path="/parent/credit"
          element={
            <PrivateRoute>
              <CreditHistoryPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/parent/credit/charge"
          element={
            <PrivateRoute>
              <CreditChargePage />
            </PrivateRoute>
          }
        />

        {/* 마이페이지 (학부모 전용) */}
        <Route
          path="/parent/profile"
          element={
            <PrivateRoute>
              <ParentProfilePage />
            </PrivateRoute>
          }
        />

        {/* 크레딧 충전 결과 — 토스 리다이렉트 대상 (인증 필요) */}
        <Route
          path="/credit/success"
          element={
            <PrivateRoute>
              <CreditSuccessPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/credit/fail"
          element={
            <PrivateRoute>
              <CreditFailPage />
            </PrivateRoute>
          }
        />

        {/* 그 외는 랜딩으로 리다이렉트 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
};

/**
 * ✅ 최상위 App 컴포넌트
 * - ErrorBoundary로 전역 에러 처리
 * - AuthProvider로 전역 인증 상태 제공
 * - Router로 라우팅 처리
 */
const App: React.FC = () => {
  // Live Update(OTA)는 index.tsx에서 처리한다 — ready() 호출이 React 마운트까지
  // 지연되면 readyTimeout을 넘겨 정상 번들이 롤백되기 때문.
  return (
    <ErrorBoundary>
      <ToastProvider>
        <PushToastBridge />
        <ConfirmProvider>
          <AuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
